import { Theme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Edge, Node } from "@xyflow/react";
import { LayoutOptions } from "elkjs/lib/elk.bundled";
import { Clue, ManagedAnswer, StepChallenge } from "../../directus/collections";
import { LangSwitchOption } from "../CityEscapeFlowChart/types";

export const useEnigmaFlow = (
  cityEscapeId: string,
  challenge: StepChallenge | null | undefined,
  {
    theme,
    direction,
  }: {
    theme: Theme;
    direction?: "DOWN" | "UP" | "RIGHT" | "LEFT";
    activeLang?: LangSwitchOption;
  }
) => {
  return useQuery({
    queryKey: ["enigmaFlowData", cityEscapeId, challenge?.id, direction],
    queryFn: async () => {
      let { nodes, edges } = convertEnigmaToFlowData(cityEscapeId, challenge!, {
        theme,
      });
      // nodes = await runLayout(nodes, edges, { direction });
      return { nodes, edges };
    },
    enabled: !!challenge,
  });
};

// Helper function to convert City Escape data to ReactFlow format
export const convertEnigmaToFlowData = (
  cityEscapeId: string,
  challenge: StepChallenge,
  {
    activeLang,
  }: {
    theme: Theme;
    expandedScenes?: Set<string>;
    activeLang?: LangSwitchOption;
  }
): {
  nodes: Node<{ challenge: StepChallenge; cityEscapeId: string }>[];
  edges: Edge[];
} => {
  const nodes: Node<{ challenge: StepChallenge; cityEscapeId: string }>[] = [];
  const edges: Edge[] = [];

  // Safety check
  if (!challenge) {
    console.warn("CityEscape is null or undefined");
    return { nodes, edges };
  }

  const initNode: Node<{ challenge: StepChallenge; cityEscapeId: string }> = {
    id: `challenge-${challenge.id}-init`,
    type: "enigmaInit",
    data: { challenge, cityEscapeId },
    position: { x: 0, y: 0 },
  };

  nodes.push(initNode);

  const {
    nodes: hintNodes,
    edges: hintEdges,
    hintsNodeId,
  } = processHints(cityEscapeId, challenge, {
    activeLang,
    source: initNode.id,
  });

  nodes.push(...hintNodes);
  edges.push(...hintEdges);

  const { nodes: inputNodes, edges: inputEdges } = processInputs(
    cityEscapeId,
    challenge,
    {
      activeLang,
      source: initNode.id,
    }
  );

  nodes.push(...inputNodes);
  edges.push(...inputEdges);

  return { nodes, edges };
};

function processHints(
  cityEscapeId: string,
  challenge: StepChallenge,
  { activeLang, source }: { activeLang?: LangSwitchOption; source: string }
) {
  const nodes: Node<{
    cityEscapeId: string;
    challenge: StepChallenge;
    hint?: Clue;
    layoutOptions?: LayoutOptions;
  }>[] = [];
  const edges: Edge[] = [];

  const hintsNodeId = `challenge-${challenge.id}-hints`;
  nodes.push({
    id: hintsNodeId,
    type: "enigmaHints",
    data: {
      challenge,
      cityEscapeId,
      layoutOptions: {
        "elk.alignment": "TOP",
        // "elk.layered.layering.layerChoiceConstraint": "0",
        "partitioning.partition": "1",
      },
    },
    position: { x: 0, y: 0 },
  });
  edges.push(createEdge(source, hintsNodeId, {}));
  return { nodes, edges, hintsNodeId };
}

function processSolution(
  cityEscapeId: string,
  challenge: StepChallenge,
  { activeLang, source }: { activeLang?: LangSwitchOption; source: string }
) {
  const nodes: Node<{
    cityEscapeId: string;
    challenge: StepChallenge;
    hint?: Clue;
  }>[] = [];
  const edges: Edge[] = [];

  const solutionNodeId = `challenge-${challenge.id}-solution`;
  nodes.push({
    id: solutionNodeId,
    type: "enigmaSolution",
    data: { challenge, cityEscapeId },
    position: { x: 0, y: 0 },
  });
  edges.push(createEdge(source, solutionNodeId, {}));

  return { nodes, edges, solutionNodeId };
}

function processInputs(
  cityEscapeId: string,
  challenge: StepChallenge,
  { activeLang, source }: { activeLang?: LangSwitchOption; source: string }
) {
  const nodes: Node<{ cityEscapeId: string; challenge: StepChallenge }>[] = [];
  const edges: Edge[] = [];

  const { nodes: userInputNodes, edges: userInputEdges } = processUserInput(
    cityEscapeId,
    challenge,
    { activeLang, source }
  );

  nodes.push(...userInputNodes);
  edges.push(...userInputEdges);

  const { nodes: managedAnswerNodes, edges: managedAnswerEdges } =
    processManagedAnswers(cityEscapeId, challenge, { activeLang, source });

  nodes.push(...managedAnswerNodes);
  edges.push(...managedAnswerEdges);

  return { nodes, edges };
}

function processManagedAnswers(
  cityEscapeId: string,
  challenge: StepChallenge,
  { activeLang, source }: { activeLang?: LangSwitchOption; source: string }
) {
  const nodes: Node<{
    cityEscapeId: string;
    challenge: StepChallenge;
    answers: ManagedAnswer[];
    layoutOptions?: LayoutOptions;
  }>[] = [];
  const answerNodeId = `challenge-${challenge.id}-managed-answer`;
  const answers = challenge.managed_answers || [];
  const edges: Edge[] = [];
  nodes.push({
    id: answerNodeId,
    type: "enigmaManagedAnswer",
    data: {
      cityEscapeId,
      challenge,
      answers,
      layoutOptions: {
        "elk.alignment": "TOP",
        "partitioning.partition": "1",
      },
    },
    position: { x: 0, y: 0 },
  });
  edges.push(createEdge(source, answerNodeId, {}));

  return { nodes, edges };
}

function processUserInput(
  cityEscapeId: string,
  challenge: StepChallenge,
  { activeLang, source }: { activeLang?: LangSwitchOption; source: string }
) {
  const nodes: Node<{
    cityEscapeId: string;
    challenge: StepChallenge;
    layoutOptions?: LayoutOptions;
  }>[] = [];
  const edges: Edge[] = [];

  const successNodeId = `challenge-${challenge.id}-success`;
  nodes.push({
    id: successNodeId,
    type: "enigmaSuccess",
    data: {
      challenge,
      cityEscapeId,
      layoutOptions: {
        "elk.alignment": "TOP",
        "partitioning.partition": "1",
      },
    },
    position: { x: 0, y: 0 },
  });
  edges.push(createEdge(source, successNodeId, {}));

  return { nodes, edges };
}

const createEdge = (
  source: string,
  target: string,
  {
    type = "smoothstep",
    label = "",
    data = {},
    theme = undefined,
    isAlternative = false,
    animated = false,
  }: {
    label?: string;
    type?: string;
    data?: any;
    theme?: Theme;
    isAlternative?: boolean;
    animated?: boolean;
  }
): Edge => ({
  id: `${source}-${target}`,
  source,
  target,
  label,
  type,
  animated,
  style: {
    strokeWidth: 2,
    strokeDasharray: isAlternative ? "5,5" : undefined,
    stroke: theme?.palette.primary.main,
    fill: theme?.palette.text.secondary,
  },
  labelStyle: {
    fontSize: "10px",
    fontWeight: "normal",
    fill: theme?.palette.text.secondary,
  },
  labelBgStyle: {
    fill: theme?.palette.background.paper,
    fillOpacity: 0.8,
  },
  data: {
    isAlternative,
    ...data,
  },
});
